import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaArrowLeft,
  FaWallet,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaChevronUp,
  FaChevronDown
} from 'react-icons/fa';
import { getVendorWalletSummary, getVendorWalletTransactions } from '../../features/vendor/vendorWalletSlice';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const VendorWalletHistory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [vendorData, setVendorData] = useState(null);
  // Use walletTransactions from Redux instead of local state
  // const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showBalances, setShowBalances] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const {
    totalWalletBalance,
    totalCommissionEarned,
    driverWallets,
    walletTransactions,
    loading: reduxLoading,
    error: reduxError
  } = useSelector((state) => state.vendorWallet);

  const itemsPerPage = 10;

  useEffect(() => {
    checkAuth();
    fetchWalletHistory();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('vendorToken');
    const vendor = localStorage.getItem('vendorData');
    
    if (!token || !vendor) {
      navigate('/vendor/login');
      return;
    }

    try {
      setVendorData(JSON.parse(vendor));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      navigate('/vendor/login');
    }
  };

  const fetchWalletHistory = async () => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) {
        console.error('No vendor data found in localStorage');
        navigate('/vendor/login');
        return;
      }

      console.log('🔍 Fetching wallet history for vendor:', vendor._id);

      // Dispatch to get latest wallet summary and transactions
      await dispatch(getVendorWalletSummary(vendor._id));

      // Try calling the commission history API directly since it might have more data
      console.log('🔄 Trying commission history API...');

      const token = localStorage.getItem('vendorToken');

      try {
        const commissionResponse = await axios.get(
          `${API_URL}/vendor/${vendor._id}/commission-history`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('�� Commission history response:', commissionResponse.data);

        if (commissionResponse.data.success || commissionResponse.data.status) {
          const commissionData = commissionResponse.data.data;
          if (commissionData && commissionData.transactions && commissionData.transactions.length > 0) {
            console.log('✅ Found commission transactions:', commissionData.transactions.length);
            // Transform commission transactions to match the expected format
            const transformedTransactions = commissionData.transactions.map(txn => ({
              _id: txn.transactionId,
              type: txn.type,
              amount: txn.amount,
              description: txn.description,
              date: txn.date || txn.createdAt,
              status: txn.status,
              driverName: txn.driver?.name,
              bookingId: txn.booking?.bookingId || txn.bookingId,
              paymentMethod: txn.paymentMethod,
              // Grouped fields
              rideAmount: txn.rideAmount,
              adminCommission: txn.adminCommission,
              gstAmount: txn.gstAmount,
              vendorCommission: txn.vendorCommission,
              paidToDriver: txn.paidToDriver,
              metadata: {
                ...(txn.metadata || {}),
                commissionPercentage: (txn.metadata && txn.metadata.commissionPercentage) != null ? txn.metadata.commissionPercentage : (txn.type === 'admin_commission_deducted' ? txn.commissionBreakdown?.adminCommission : undefined),
                vendorCommissionPercentage: (txn.metadata && txn.metadata.vendorCommissionPercentage) != null ? txn.metadata.vendorCommissionPercentage : (txn.type === 'vendor_commission_credited' ? txn.commissionBreakdown?.vendorCommission : undefined)
              }
            }));

            // Manually update Redux state with commission transactions
            dispatch({
              type: 'vendorWallet/getVendorWalletTransactions/fulfilled',
              payload: { transactions: transformedTransactions }
            });

            console.log('Commission transactions loaded:', transformedTransactions.length);
            return;
          } else {
            console.log('📝 No commission transactions found');
          }
        } else {
          console.log('⚠️ Commission API response indicates failure:', commissionResponse.data);
        }
      } catch (commissionError) {
        console.log('❌ Commission history API failed:', commissionError.response?.data || commissionError.message);
        toast.warning('Commission history API unavailable. Trying wallet transactions...');
      }

      // Fallback to wallet transactions if commission history is empty
      const transactionResult = await dispatch(getVendorWalletTransactions({
        vendorId: vendor._id,
        page: 1,
        limit: 100 // Get more transactions initially
      }));

      console.log('📥 Transaction result:', transactionResult);

      if (transactionResult.type.includes('fulfilled')) {
        const transactionData = transactionResult.payload;
        console.log('✅ Transactions loaded via Redux:', transactionData);

        if (transactionData && transactionData.transactions) {
          console.log('Transaction count:', transactionData.transactions.length);
          if (transactionData.transactions.length === 0) {
            console.log('📝 No transactions found in database');
            toast.info('No transaction history found. Complete some rides to see transaction history.');
          }
        }
      } else if (transactionResult.type.includes('rejected')) {
        console.log('⚠️ Failed to load transactions:', transactionResult.payload);
        toast.warning('Failed to load transaction history: ' + (transactionResult.payload || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error fetching wallet history:', error);

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const message = error.response.data?.message || 'Unknown error';

        console.error(`API Error ${status}:`, message);

        if (status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('vendorToken');
          localStorage.removeItem('vendorData');
          navigate('/vendor/login');
          return;
        } else if (status === 403) {
          toast.error('Access denied. Please check your permissions.');
        } else if (status === 404) {
          toast.error('Vendor not found or endpoint not available.');
        } else {
          toast.error(`Failed to load wallet history: ${message}`);
        }
      } else if (error.request) {
        // Network error
        console.error('Network error:', error.request);
        toast.error('Cannot connect to server. Please check if backend is running on port 5000.');
      } else {
        // Other error
        console.error('Request setup error:', error.message);
        toast.error('Failed to load wallet history');
      }

      // Error handled by Redux state
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount) || 0);
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `${num}%` : 'N/A';
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'ride_transaction':
        return <FaMoneyBillWave className="w-5 h-5 text-green-600" />;
      case 'ride_payment_received':
        return <FaMoneyBillWave className="w-5 h-5 text-green-600" />;
      case 'admin_commission_deducted':
        return <FaArrowDown className="w-5 h-5 text-red-600" />;
      case 'gst_deducted':
        return <FaArrowDown className="w-5 h-5 text-orange-500" />;
      case 'vendor_commission_credited':
        return <FaArrowUp className="w-5 h-5 text-green-600" />;
      case 'paid_to_driver':
        return <FaArrowUp className="w-5 h-5 text-blue-600" />;
      case 'commission_earned':
        return <FaMoneyBillWave className="w-4 h-4 text-green-600" />;
      case 'wallet_added':
        return <FaArrowUp className="w-4 h-4 text-blue-600" />;
      case 'withdrawal_request':
        return <FaArrowDown className="w-4 h-4 text-red-600" />;
      default:
        return <FaWallet className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FaClock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FaTimesCircle className="w-3 h-3 mr-1" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <FaSpinner className="w-3 h-3 mr-1" />
            Unknown
          </span>
        );
    }
  };

  // Ensure walletTransactions is always an array before filtering
  const filteredTransactions = (Array.isArray(walletTransactions) ? walletTransactions : []).filter(transaction => {
    // Hide COD entries except vendor commission credited and grouped ride transactions
    const method = (transaction.paymentMethod || transaction.paymentMode || '').toLowerCase();
    if (method === 'cod') {
      if (transaction.type !== 'vendor_commission_credited' && transaction.type !== 'ride_transaction') return false;
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      let bIdStr = '';
      if (typeof transaction.bookingId === 'object' && transaction.bookingId !== null) {
        bIdStr = transaction.bookingId.bookingId || transaction.bookingId.customBookingId || transaction.bookingId._id || '';
      } else if (transaction.bookingId) {
        bIdStr = String(transaction.bookingId);
      }
      const matches =
        transaction.description?.toLowerCase().includes(searchLower) ||
        transaction.driverName?.toLowerCase().includes(searchLower) ||
        bIdStr.toLowerCase().includes(searchLower);
      if (!matches) return false;
    }

    // Apply type filter
    if (filterType !== 'all') {
      if (transaction.type === 'ride_transaction') {
        // Grouped transaction matches if the corresponding component has a value
        if (filterType === 'ride_payment_received' && !(transaction.rideAmount > 0)) return false;
        if (filterType === 'admin_commission_deducted' && !(transaction.adminCommission !== 0)) return false;
        if (filterType === 'gst_deducted' && !(transaction.gstAmount !== 0)) return false;
        if (filterType === 'vendor_commission_credited' && !(transaction.vendorCommission > 0)) return false;
        if (filterType === 'paid_to_driver' && !(transaction.paidToDriver > 0)) return false;
        
        // If they select legacy type or other types not present in ride_transaction
        if (!['ride_payment_received', 'admin_commission_deducted', 'gst_deducted', 'vendor_commission_credited', 'paid_to_driver'].includes(filterType)) {
          return false;
        }
      } else if (transaction.type !== filterType) {
        return false;
      }
    }

    // Apply date filter
    if (dateRange !== 'all') {
      const transactionDate = new Date(transaction.date);
      const now = new Date();
      const daysDiff = Math.floor((now - transactionDate) / (1000 * 60 * 60 * 24));

      switch (dateRange) {
        case 'today':
          if (daysDiff !== 0) return false;
          break;
        case 'week':
          if (daysDiff > 7) return false;
          break;
        case 'month':
          if (daysDiff > 30) return false;
          break;
      }
    }

    return true;
  });

  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  if (loading || reduxLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
          <p className="text-indigo-600 font-medium">Loading wallet history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg shadow-xl border-b border-white/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/vendor/dashboard')}
                className="p-2 rounded-xl bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors"
              >
                <FaArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Wallet History</h1>
                <p className="text-sm text-gray-600">Track your transactions and earnings</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Welcome back,</p>
              <p className="font-semibold text-gray-900">{vendorData?.email || vendorData?.fullName}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Wallet Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Commission Earned</p>
                <p className="text-2xl font-bold text-green-600">
                  {showBalances ? formatCurrency(totalCommissionEarned || 0) : "₹****"}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <FaMoneyBillWave className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
              </div>

              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="ride_payment_received">🟢 Ride Payment Received</option>
                <option value="admin_commission_deducted">🔴 Admin Commission Deducted</option>
                <option value="gst_deducted">🟠 GST Deducted</option>
                <option value="vendor_commission_credited">🟢 Vendor Commission Credited</option>
                <option value="paid_to_driver">🔵 PAID To Driver</option>
                <option value="commission_earned">Commission Earned (Legacy)</option>
                <option value="wallet_added">Money Added</option>
                <option value="withdrawal_request">Withdrawals</option>
              </select>

              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transaction Breakdown Info */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">💡 Transaction History Explanation</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="font-medium">Ride Payment Received:</span>
              <span className="text-gray-600">Total fare amount</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="font-medium">Admin Commission:</span>
              <span className="text-gray-600">Platform fee deducted</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="font-medium">Your Commission:</span>
              <span className="text-gray-600">Your earning credited</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="font-medium">PAID To Driver:</span>
              <span className="text-gray-600">Amount paid to driver</span>
            </div>
          </div>
          <div className="mt-3 text-xs text-blue-700 bg-blue-100 rounded-lg p-2">
            <strong>How it works:</strong> For each ride, you'll see 4 transactions showing the complete money flow -
            payment received, platform fee deducted, your commission credited, and amount paid to driver.
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
              <span className="text-sm text-gray-600">
                Showing {filteredTransactions.length} transactions
              </span>
            </div>

            {/* Quick Summary */}
            {filteredTransactions.length > 0 && (
              <div className="grid grid-cols-4 gap-4 text-xs">
                <div className="text-center p-2 bg-green-50 rounded">
                  <div className="text-green-600 font-semibold">
                    ₹{filteredTransactions
                      .reduce((sum, t) => sum + (t.rideAmount || (t.type === 'ride_payment_received' ? t.amount : 0) || 0), 0)
                      .toLocaleString()}
                  </div>
                  <div className="text-gray-500">Payments Received</div>
                </div>
                <div className="text-center p-2 bg-red-50 rounded">
                  <div className="text-red-600 font-semibold">
                    ₹{filteredTransactions
                      .reduce((sum, t) => sum + Math.abs(t.adminCommission || (t.type === 'admin_commission_deducted' ? t.amount : 0) || 0), 0)
                      .toLocaleString()}
                  </div>
                  <div className="text-gray-500">Admin Commission</div>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <div className="text-green-600 font-semibold">
                    ₹{filteredTransactions
                      .reduce((sum, t) => sum + (t.vendorCommission || (t.type === 'vendor_commission_credited' ? t.amount : 0) || 0), 0)
                      .toLocaleString()}
                  </div>
                  <div className="text-gray-500">Your Commission</div>
                </div>
                <div className="text-center p-2 bg-blue-50 rounded">
                  <div className="text-blue-600 font-semibold">
                    ₹{filteredTransactions
                      .reduce((sum, t) => sum + (t.paidToDriver || (t.type === 'paid_to_driver' ? t.amount : 0) || 0), 0)
                      .toLocaleString()}
                  </div>
                  <div className="text-gray-500">Paid To Driver</div>
                </div>
              </div>
            )}
          </div>

          {paginatedTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Booking ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedTransactions.map((transaction) => {
                    const isExpanded = !!expandedRows[transaction._id];
                    return (
                      <React.Fragment key={transaction._id}>
                        <tr 
                          className={`hover:bg-gray-50 transition-colors ${
                            transaction.type === 'ride_transaction' ? 'cursor-pointer font-medium' : ''
                          }`}
                          onClick={() => transaction.type === 'ride_transaction' && toggleRow(transaction._id)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="mr-3">
                                {getTransactionIcon(transaction.type)}
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                                  {transaction.type === 'ride_transaction' ? (
                                    <>
                                      {transaction.description.split(' - ')[0]}
                                      <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full font-bold border border-indigo-100 flex items-center gap-1">
                                        Breakdown
                                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                      </span>
                                    </>
                                  ) : (
                                    transaction.type === 'ride_payment_received' ? 'Ride Payment Received' :
                                    transaction.type === 'admin_commission_deducted' ? 'Admin Commission Deducted' :
                                    transaction.type === 'gst_deducted' ? 'GST Deducted' :
                                    transaction.type === 'vendor_commission_credited' ? 'Vendor Commission Credited' :
                                    transaction.type === 'paid_to_driver' ? 'PAID To Driver' :
                                    transaction.description
                                  )}
                                </div>
                                {transaction.driverName && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    Driver: {transaction.driverName}
                                  </div>
                                )}
                                {transaction.type === 'ride_transaction' && (
                                  <div className="text-[11px] text-gray-400 mt-0.5">
                                    {transaction.description.split(' - ')[1] || `Booking #${transaction.bookingId}`}
                                  </div>
                                )}
                                {transaction.type === 'admin_commission_deducted' && (
                                  <div className="text-xs text-gray-400">
                                    Platform fee ({formatPercentage(transaction.metadata?.commissionPercentage)})
                                  </div>
                                )}
                                {transaction.type === 'vendor_commission_credited' && (
                                  <div className="text-xs text-gray-400">
                                    Your earning ({formatPercentage(transaction.metadata?.vendorCommissionPercentage)})
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-bold ${
                              transaction.type === 'admin_commission_deducted' ? 'text-red-600' :
                              (transaction.amount > 0 ? 'text-green-600' : 'text-red-600')
                            }`}>
                              {transaction.type === 'admin_commission_deducted' ?
                                (showBalances ? `-${formatCurrency(Math.abs(transaction.amount))}` : "-₹****") :
                                `${transaction.amount > 0 ? '+' : ''}${showBalances ? formatCurrency(transaction.amount) : "₹****"}`
                              }
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex flex-col">
                              <span>{new Date(transaction.date).toLocaleDateString('en-IN')}</span>
                              <span className="text-xs text-gray-500">{new Date(transaction.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(transaction.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                            {typeof transaction.bookingId === 'object' && transaction.bookingId !== null
                              ? (transaction.bookingId.bookingId || transaction.bookingId.customBookingId || transaction.bookingId._id || '-')
                              : (transaction.bookingId || '-')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-950 font-bold uppercase">
                            {transaction.paymentMethod || '-'}
                          </td>
                        </tr>
                        {transaction.type === 'ride_transaction' && isExpanded && (
                          <tr className="bg-slate-50/50">
                            <td colSpan={6} className="px-8 py-4 border-t border-b border-gray-100">
                              <div className="max-w-4xl">
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 pl-1">
                                  Itemized Ride Payment Breakdown
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                                  {/* 1. Total Fare */}
                                  <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Ride Fare</span>
                                    <span className="text-base font-extrabold text-slate-800 block mt-1">
                                      {formatCurrency(transaction.rideAmount)}
                                    </span>
                                  </div>
                                  {/* 2. Admin Commission */}
                                  <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                                    <span className="text-[10px] font-bold text-red-500/80 uppercase tracking-wider block">Platform Fee</span>
                                    <span className="text-base font-extrabold text-red-600 block mt-1">
                                      -{formatCurrency(transaction.adminCommission)}
                                    </span>
                                  </div>
                                  {/* 3. GST Tax */}
                                  <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm">
                                    <span className="text-[10px] font-bold text-orange-500/80 uppercase tracking-wider block">GST Tax</span>
                                    <span className="text-base font-extrabold text-orange-600 block mt-1">
                                      -{formatCurrency(transaction.gstAmount)}
                                    </span>
                                  </div>
                                  {/* 4. Vendor Commission */}
                                  <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm bg-gradient-to-br from-white to-emerald-50/10">
                                    <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider block">Your Commission</span>
                                    <span className="text-base font-black text-green-600 block mt-1">
                                      +{formatCurrency(transaction.vendorCommission)}
                                    </span>
                                  </div>
                                  {/* 5. Paid To Driver */}
                                  <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-sm col-span-2 sm:col-span-1">
                                    <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block">Paid to Driver</span>
                                    <span className="text-base font-extrabold text-indigo-600 block mt-1">
                                      {formatCurrency(transaction.paidToDriver)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <FaWallet className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
              <p className="text-gray-600">
                {searchTerm || filterType !== 'all' || dateRange !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Your transaction history will appear here once you start earning commissions'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of{' '}
                {filteredTransactions.length} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === page
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorWalletHistory;
